// Arma las páginas del PDF de Reportes Recepción a partir de construirReporteRecepcion
// (exportacionTablas.js): portada con filtros, indicadores y alertas, y después una página por
// pestaña con cada reporte como gráfico + tabla. El Excel sale de la misma estructura, así que
// los dos documentos siempre traen los mismos reportes con los mismos números.
import autoTable from 'jspdf-autotable';
import { construirReporteExportacion } from './exportacionReporte';
import { imagenGrafico } from './exportacionGrafico';

// Las fuentes estándar de jsPDF (WinAnsi) no tienen algunos símbolos: se reemplazan antes de
// escribir para que no salgan como caracteres basura.
const REEMPLAZOS_TEXTO = [[/≤/g, '<='], [/≥/g, '>='], [/→/g, '->'], [/·/g, '-']];

function limpiarTexto(valor) {
    return REEMPLAZOS_TEXTO.reduce((texto, [patron, reemplazo]) => texto.replace(patron, reemplazo), String(valor));
}

export function formatearCelda(valor) {
    if (valor === null || valor === undefined || valor === '') return '—';
    if (typeof valor === 'number') {
        if (Number.isNaN(valor)) return '—';
        return Number.isInteger(valor)
            ? valor.toLocaleString('es-AR')
            : valor.toLocaleString('es-AR', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
    }
    return limpiarTexto(valor);
}

/**
 * @param {import('jspdf').jsPDF} doc documento con el encabezado ya dibujado (ver ReportesPDF.js)
 * @param {object} config datos de la pantalla (ver datosParaExportar en ReportesRecepcion.js)
 * @param {{colorPrimario: number[]}} conf
 * @param {{pageW: number, pageH: number, m: number}} medidas
 */
export function generarPaginasRecepcion(doc, config, conf, { pageW, pageH, m }) {
    const reporte = construirReporteExportacion(config);
    const { pestanas } = reporte;
    const anchoUtil = pageW - m * 2;
    const colorPrimario = conf.colorPrimario;
    let y = 48;

    const asegurarEspacio = (alto) => {
        if (y + alto > pageH - 16) {
            doc.addPage();
            y = 20;
        }
    };

    const texto = (contenido, { tam = 9, color = [95, 99, 108], negrita = false, espacio = 3 } = {}) => {
        doc.setFontSize(tam);
        doc.setFont('helvetica', negrita ? 'bold' : 'normal');
        doc.setTextColor(...color);
        const lineas = doc.splitTextToSize(limpiarTexto(contenido), anchoUtil);
        const altoLinea = tam * 0.43;
        asegurarEspacio(lineas.length * altoLinea + espacio);
        doc.text(lineas, m, y + altoLinea);
        y += lineas.length * altoLinea + espacio;
    };

    const tabla = (columnas, filas, opciones = {}) => {
        autoTable(doc, {
            head: [columnas.map(limpiarTexto)],
            body: filas.map((fila) => fila.map(formatearCelda)),
            startY: y,
            margin: { left: m, right: m },
            headStyles: { fillColor: colorPrimario, fontSize: 8, halign: 'left' },
            styles: { fontSize: 8, cellPadding: 1.6, overflow: 'linebreak' },
            alternateRowStyles: { fillColor: [247, 248, 250] },
            ...opciones,
        });
        y = doc.lastAutoTable.finalY + 7;
    };

    const grafico = (definicion) => {
        const imagen = imagenGrafico(definicion);
        if (!imagen) return;
        const alto = anchoUtil * ((definicion.alto || 440) / (definicion.ancho || 1000));
        asegurarEspacio(alto + 4);
        doc.addImage(imagen, 'JPEG', m, y, anchoUtil, alto);
        y += alto + 5;
    };

    // ── Portada ──────────────────────────────────────────────────────────────
    tabla(['Filtros aplicados', ''], reporte.filtros, { columnStyles: { 0: { cellWidth: 55, fontStyle: 'bold' } } });
    if (reporte.kpis.length > 0) {
        tabla(['Indicadores del período', 'Valor'], reporte.kpis, { columnStyles: { 0: { cellWidth: 85 } } });
    }
    if (reporte.alertas.length > 0) {
        tabla(['Alertas', 'Detalle'], reporte.alertas.map((a) => [a.titulo, a.texto]), {
            headStyles: { fillColor: [181, 39, 26], fontSize: 8 },
            columnStyles: { 0: { cellWidth: 55, fontStyle: 'bold' } },
        });
    }
    texto(`Secciones incluidas: ${pestanas.map((p) => p.titulo).join(', ') || 'ninguna'}.`, { tam: 8.5 });

    // ── Una página por pestaña ───────────────────────────────────────────────
    pestanas.forEach((pestana) => {
        doc.addPage();
        doc.setFillColor(...colorPrimario);
        doc.rect(0, 10, pageW, 13, 'F');
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(13);
        doc.setTextColor(255, 255, 255);
        doc.text(limpiarTexto(pestana.titulo.toUpperCase()), m, 18.5);
        y = 30;
        if (pestana.nota) texto(pestana.nota, { tam: 8.5 });

        pestana.secciones.forEach((seccion) => {
            // El título no puede quedar solo al pie de la página con su gráfico en la hoja
            // siguiente: se reserva lugar para el título más el gráfico (o las primeras filas de
            // la tabla) antes de escribirlo.
            const altoGrafico = seccion.grafico && seccion.filas.length > 0
                ? anchoUtil * ((seccion.grafico.alto || 440) / (seccion.grafico.ancho || 1000))
                : 0;
            asegurarEspacio(18 + (altoGrafico || 24));
            texto(seccion.titulo.toUpperCase(), { tam: 11, color: colorPrimario, negrita: true, espacio: 1.5 });
            if (seccion.nota) texto(seccion.nota, { tam: 8.5 });
            if (seccion.filas.length === 0) {
                texto(seccion.vacio || 'Sin datos en el período seleccionado.', { tam: 9, espacio: 6 });
                return;
            }
            if (seccion.grafico) grafico(seccion.grafico);
            tabla(seccion.columnas, seccion.filas, seccion.opcionesTabla || {});
        });
    });

    // ── Numeración ───────────────────────────────────────────────────────────
    const totalPaginas = doc.getNumberOfPages();
    for (let pagina = 1; pagina <= totalPaginas; pagina += 1) {
        doc.setPage(pagina);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        doc.setTextColor(140, 140, 140);
        doc.text(`Reporte de Recepción - página ${pagina} de ${totalPaginas}`, pageW - m, pageH - 8, { align: 'right' });
    }

    return doc;
}
